import React, { useEffect } from "react";
import { motion } from "framer-motion";
import { CircularProgressbar, buildStyles } from "react-circular-progressbar";
import "react-circular-progressbar/dist/styles.css";
import Header from "../../components/Header/Header";
import Footer from "../../components/Footer/Footer";
import ERPChatbot from "../../components/ERPChatbot/ERPChatbot";
import logistics from "../../assets/logistics.png";
import "./Logistics.css";

export default function Logistics() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const logisticsStats = [
    { title: "On-Time Deliveries", value: 98 },
    { title: "Fleet Utilization", value: 94 },
    { title: "Operational Accuracy", value: 96 },
    { title: "Customer Satisfaction", value: 97 },
  ];

  const logisticsCapabilities = [
    {
      title: "Smart Fleet Tracking",
      desc: "Real-time tracking and predictive maintenance to optimize transport performance.",
      icon: "🚛",
    },
    {
      title: "Integrated Warehousing",
      desc: "Seamlessly connect storage, inventory, and delivery for smooth operations.",
      icon: "🏭",
    },
    {
      title: "Global Freight Solutions",
      desc: "AI-driven route optimization for reliable and transparent logistics worldwide.",
      icon: "🌍",
    },
    {
      title: "Insightful Analytics",
      desc: "Real-time dashboards to make informed, data-driven logistics decisions.",
      icon: "📊",
    },
  ];

  return (
    <>
      <Header />
      <div className="logistics-page">
        {/* HERO SECTION */}
        <section
          className="logistics-hero"
          style={{ backgroundImage: `url(${logistics})` }}
        >
          <div className="logistics-hero-overlay"></div>
          <div className="logistics-hero-content">
            <motion.h1
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 1 }}
            >
              Logistics Solutions that Move Your Business Forward
            </motion.h1>
            <motion.p
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 1.2 }}
            >
              From manufacturing floors to customer doors — we connect your
              supply chain with speed, precision, and visibility.
            </motion.p>
          </div>
        </section>

        {/* INTRO SECTION */}
        <section className="logistics-intro">
          <h2>Streamlined Supply Chain, Smarter Future</h2>
          <p>
            Larch Logistics redefines operational efficiency with AI-powered
            planning, seamless warehouse integration, and real-time visibility.
            Empower your logistics to deliver more — with less effort.
          </p>
        </section>

        {/* CAPABILITIES SECTION */}
        <section className="logistics-capabilities">
          <h2>Our Logistics Capabilities</h2>
          <div className="logistics-capability-cards">
            {logisticsCapabilities.map((cap, index) => (
              <motion.div
                key={index}
                className="logistics-capability-card"
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.2 }}
                viewport={{ once: true }}
              >
                <div className="capability-icon">{cap.icon}</div>
                <h3>{cap.title}</h3>
                <p>{cap.desc}</p>
              </motion.div>
            ))}
          </div>
        </section>

        {/* STATS SECTION */}
        <section className="logistics-stats">
          <h2>Driving Measurable Impact</h2>
          <div className="stats-grid">
            {logisticsStats.map((stat, index) => (
              <motion.div
                key={index}
                className="stat-card"
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.2 }}
                viewport={{ once: true }}
              >
                <CircularProgressbar
                  value={stat.value}
                  text={`${stat.value}%`}
                  styles={buildStyles({
                    pathColor: "teal",
                    textColor: "#222",
                    trailColor: "#eee",
                  })}
                />
                <p>{stat.title}</p>
              </motion.div>
            ))}
          </div>
        </section>

        {/* WHY CHOOSE US SECTION */}
        <section className="logistics-why">
          <div className="why-text">
            <h2>Why Choose Larch Logistics?</h2>
            <p>
              We blend technology and strategy to ensure every shipment, route,
              and decision contributes to your success. From automated dispatch
              to AI-driven insights — our solutions guarantee efficiency that
              scales.
            </p>
            <ul>
              <li>✔ Predictive route optimization</li>
              <li>✔ Real-time performance analytics</li>
              <li>✔ Smart resource utilization</li>
              <li>✔ End-to-end transparency</li>
            </ul>
          </div>
          <div className="why-image">
            <img src="/images/logistics-team.jpg" alt="Larch Logistics Team" />
          </div>
        </section>

        <ERPChatbot />
      </div>
      <Footer />
    </>
  );
}
